import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import myAxios from "../AxiosUrl";
import styles from "./TournamentFixture.module.css";
import Loading from "../Loading/Loading";

const TournamentFixture = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFixtures, setTotalFixtures] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalFixtures: 0,
    totalCompetitions: 0,
    totalTeams: 0,
    completedMatches: 0,
    upcomingMatches: 0,
  });

  // Filters
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [selectedHomeTeam, setSelectedHomeTeam] = useState("");
  const [selectedAwayTeam, setSelectedAwayTeam] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [teams, setTeams] = useState([]);

  // Modal state
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fixtureToDelete, setFixtureToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchFixtures();
  }, [
    currentPage,
    searchTerm,
    selectedCompetition,
    selectedHomeTeam,
    selectedAwayTeam,
    selectedStatus,
  ]);

  const fetchFixtures = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        competitionName: selectedCompetition,
        homeTeam: selectedHomeTeam,
        awayTeam: selectedAwayTeam,
        status: selectedStatus,
      });

      const response = await myAxios.get(`/tournament/fixtures?${params}`);
      const data = response.data;

      setFixtures(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalFixtures(data.pagination?.totalFixtures || 0);

      // Update statistics from the main response
      setStats({
        totalFixtures: data.totalFixtures || 0,
        totalCompetitions: data.totalCompetitions || 0,
        totalTeams: data.totalTeams || 0,
        completedMatches: data.completedMatches || 0,
        upcomingMatches: data.upcomingMatches || 0,
      });

      // Extract unique teams and competitions
      const uniqueTeams = [];
      const uniqueCompetitions = [];

      data.data?.forEach((fixture) => {
        if (fixture.homeTeam?.name) uniqueTeams.push(fixture.homeTeam.name);
        if (fixture.awayTeam?.name) uniqueTeams.push(fixture.awayTeam.name);
        if (fixture.competition?.name)
          uniqueCompetitions.push(fixture.competition.name);
      });

      setTeams([...new Set(uniqueTeams)]);
      setCompetitions([...new Set(uniqueCompetitions)]);
    } catch (err) {
      setError("Error fetching tournament fixtures");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFixtureClick = async (fixtureUuid) => {
    try {
      const response = await myAxios.get(`/tournament/fixtures/${fixtureUuid}`);
      const data = response.data;
      setSelectedFixture(data.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching fixture details:", err);
    }
  };

  const handleDeleteClick = (fixture) => {
    setFixtureToDelete(fixture);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fixtureToDelete) return;

    try {
      setDeleting(true);
      const response = await myAxios.delete(
        `/tournament/fixtures/${fixtureToDelete.fixtureUuid}`
      );

      if (response.data.success) {
        // Refresh the fixtures data
        fetchFixtures();
        alert("Fixture deleted successfully!");
      }
    } catch (err) {
      console.error("Error deleting fixture:", err);
      alert("Failed to delete fixture. Please try again.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setFixtureToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setFixtureToDelete(null);
  };

const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSelectedCompetition("");
    setSelectedHomeTeam("");
    setSelectedAwayTeam("");
    setSelectedStatus("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    const colors = {
      Result: "#28a745",
      Fixture: "#007bff",
      Live: "#dc3545",
      Postponed: "#ffc107",
      Cancelled: "#6c757d",
    };
    return colors[status] || "#6c757d";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    // Format in Turkish timezone
    return date.toLocaleDateString("tr-TR", {
      timeZone: "Europe/Istanbul",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={styles.paginationBtn}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="start-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`${styles.paginationBtn} ${currentPage === i ? styles.active : ""}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="end-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={styles.paginationBtn}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className={styles.tournamentFixturesContainer}>
        <div className={styles.tournamentFixturesContent}>
          <div className={styles.errorMessage}>
            <span className="material-icons">error</span>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tournamentFixturesContainer}>
      <div className={styles.tournamentFixturesContent}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Tournament Fixtures</h1>
          <p className={styles.pageSubtitle}>
            Manage tournament fixtures and matches
          </p>
        </div>

        {/* Statistics Section */}
        <div className={styles.statsSection}>
          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={`${styles.statIcon} ${styles.fixtures}`}>
              <span className="material-icons">event</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalFixtures}</div>
              <div className={styles.statLabel}>Total Fixtures</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className={`${styles.statIcon} ${styles.competitions}`}>
              <span className="material-icons">sports_soccer</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalCompetitions}</div>
              <div className={styles.statLabel}>Competitions</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className={`${styles.statIcon} ${styles.completed}`}>
              <span className="material-icons">check_circle</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.completedMatches}</div>
              <div className={styles.statLabel}>Completed</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <div className={`${styles.statIcon} ${styles.upcoming}`}>
              <span className="material-icons">schedule</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.upcomingMatches}</div>
              <div className={styles.statLabel}>Upcoming</div>
            </div>
          </motion.div>
        </div>

        {/* Compact Filters Section */}
        <div className={styles.compactFiltersSection}>
          <div className={styles.filtersRow}>
            {/* Search Input */}
            <div className={styles.compactSearch}>
              <div className={styles.searchInputWrapper}>
                <span className={`material-icons ${styles.searchIcon}`}>search</span>
                <input
                  type="text"
                  placeholder="Search fixtures..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className={styles.compactSearchInput}
                />
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className={styles.compactFilters}>
              <div className={styles.filterDropdown}>
                <select
                  value={selectedCompetition}
                  onChange={(e) => {
                    setSelectedCompetition(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Competition</option>
                  {competitions.map((comp) => (
                    <option key={comp} value={comp}>
                      {comp}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedHomeTeam}
                  onChange={(e) => {
                    setSelectedHomeTeam(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Home Team</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedAwayTeam}
                  onChange={(e) => {
                    setSelectedAwayTeam(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Away Team</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Status</option>
                  <option value="Result">Result</option>
                  <option value="Fixture">Fixture</option>
                  <option value="Live">Live</option>
                  <option value="Postponed">Postponed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>
            </div>

            {/* Clear Filters Button */}
            <button
              onClick={clearFilters}
              className={styles.compactClearBtn}
              title="Clear all filters"
            >
              <span className="material-icons">clear</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          <table className={styles.fixturesTable}>
            <thead>
              <tr>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>event</span>
                  Match
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    sports_soccer
                  </span>
                  Competition
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>today</span>
                  Date
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>score</span>
                  Result
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>info</span>
                  Status
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>settings</span>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {fixtures.map((fixture) => (
                <motion.tr
                  key={fixture.fixtureUuid}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="table-row"
                >
                  <td className={styles.matchCell}>
                    <div className={styles.matchInfo}>
                      <div className={styles.teamsContainer}>
                        <div className={styles.team}>
                          <div className={styles.teamLogo}>
                            {fixture.homeTeam?.teamLogoUrl ? (
                              <img
                                src={fixture.homeTeam.teamLogoUrl}
                                alt={fixture.homeTeam.name}
                                className={styles.teamImage}
                              />
                            ) : (
                              <span className="material-icons">shield</span>
                            )}
                          </div>
                          <div className={styles.teamInfo}>
                            <span className={styles.teamName}>
                              {fixture.homeTeam?.name || "TBD"}
                            </span>
                          </div>
                        </div>
                        <div className={styles.vsSeparator}>VS</div>
                        <div className={styles.team}>
                          <div className={styles.teamLogo}>
                            {fixture.awayTeam?.teamLogoUrl ? (
                              <img
                                src={fixture.awayTeam.teamLogoUrl}
                                alt={fixture.awayTeam.name}
                                className={styles.teamImage}
                              />
                            ) : (
                              <span className="material-icons">shield</span>
                            )}
                          </div>
                          <div className={styles.teamInfo}>
                            <span className={styles.teamName}>
                              {fixture.awayTeam?.name || "TBD"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.competitionBadge}>
                      {fixture.competition?.name || "N/A"}
                    </div>
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <span className={styles.matchDate}>
                        {formatDate(fixture.date)}
                      </span>
                      {fixture.time && (
                        <span className={styles.matchTime}>{fixture.time}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.resultDisplay}>
                      {fixture.status === "Result" && (
                        <span className={styles.score}>
                          {fixture.liveData?.homeScore || 0} -{" "}
                          {fixture.liveData?.awayScore || 0}
                        </span>
                      )}
                      {fixture.status === "Fixture" && (
                        <span className={styles.upcoming}>vs</span>
                      )}
                      {fixture.status === "Live" && (
                        <span className={styles.liveScore}>
                          {fixture.liveData?.homeScore || 0} -{" "}
                          {fixture.liveData?.awayScore || 0}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: getStatusColor(fixture.status),
                      }}
                    >
                      {fixture.status || "Unknown"}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actionsGroup}>
                      <button
                        onClick={() => handleFixtureClick(fixture.fixtureUuid)}
                        className={`${styles.actionBtn} ${styles.viewBtn}`}
                        title="View Details"
                      >
                        <span className="material-icons">visibility</span>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(fixture)}
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        title="Delete Fixture"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          {fixtures.length === 0 && (
            <div className={styles.emptyState}>
              <span className="material-icons empty-icon">search_off</span>
              <h3>No fixtures found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationControls}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_left</span>
              </button>
              {renderPagination()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && fixtureToDelete && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className={styles.deleteModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.deleteModalHeader}>
              <h3>Confirm Delete</h3>
              <span className={`material-icons ${styles.deleteIcon}`}>warning</span>
            </div>
            <div className={styles.deleteModalBody}>
              <p>Are you sure you want to delete this fixture?</p>
              <div className={styles.fixtureDeleteInfo}>
                <strong>
                  {fixtureToDelete.description ||
                    `${fixtureToDelete.homeTeam?.name} vs ${fixtureToDelete.awayTeam?.name}`}
                </strong>
                <span>Competition: {fixtureToDelete.competition?.name}</span>
                <span>Date: {formatDate(fixtureToDelete.date)}</span>
              </div>
              <p className={styles.deleteWarning}>This action cannot be undone.</p>
            </div>
            <div className={styles.deleteModalActions}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={styles.btnCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className={styles.btnDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className={`material-icons ${styles.rotating}`}>
                      hourglass_empty
                    </span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <span className="material-icons">delete</span>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showModal && selectedFixture && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                {selectedFixture.description ||
                  `${selectedFixture.homeTeam?.name} vs ${selectedFixture.awayTeam?.name}`}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className={styles.modalClose}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.fixtureDetailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Competition:</span>
                  <span className={styles.detailValue}>
                    {selectedFixture.competition?.name || "N/A"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Date:</span>
                  <span className={styles.detailValue}>
                    {formatDate(selectedFixture.date)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Status:</span>
                  <span className={styles.detailValue}>
                    {selectedFixture.status || "Unknown"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Venue:</span>
                  <span className={styles.detailValue}>
                    {selectedFixture.venue?.name || "TBD"}
                  </span>
                </div>
                {selectedFixture.status === "Result" && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Final Score:</span>
                    <span className={`${styles.detailValue} ${styles.scoreLarge}`}>
                      {selectedFixture.liveData?.homeScore || 0} -{" "}
                      {selectedFixture.liveData?.awayScore || 0}
                    </span>
                  </div>
                )}
              </div>

              {/* Team Schedule Section */}
              {selectedFixture.scheduleData && selectedFixture.scheduleData.length > 0 && (
                <div className={styles.scheduleSection}>
                  <h3 className={styles.scheduleSectionTitle}>
                    <span className="material-icons">event</span>
                    Team Schedules ({selectedFixture.scheduleData.length} matches)
                  </h3>
                  <div className={styles.scheduleList}>
                    {selectedFixture.scheduleData.map((match, index) => (
                      <div key={index} className={styles.scheduleItem}>
                        <div className={styles.scheduleDate}>
                          {match.date ? formatDate(match.date) : "TBD"}
                        </div>
                        <div className={styles.scheduleTeams}>
                          <span className={styles.teamName}>
                            {match.homeTeam?.name || match.home || "Team A"}
                          </span>
                          <span className={styles.vs}>vs</span>
                          <span className={styles.teamName}>
                            {match.awayTeam?.name || match.away || "Team B"}
                          </span>
                        </div>
                        <div className={styles.scheduleVenue}>
                          <span className="material-icons">location_on</span>
                          {match.venue || "Venue TBD"}
                        </div>
                        {match.competition && (
                          <div className={styles.scheduleCompetition}>
                            {match.competition}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentFixture;
